import React, { useMemo, useState } from 'react';
import AIStrologer from './AIStrologer';

function LifeTimeline({ chartData, framework = 'parasara', onRequireSignup }) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [aistrologerOpen, setAistrologerOpen] = useState(null); // {mahadasha, antardasha}

  const timelineData = useMemo(() => {
    if (!chartData) return null;

    if (framework === 'parasara' && chartData.dashas) {
      const dashas = chartData.dashas;
      const currentIdx = dashas.currentDashaIndex;
      const sequence = dashas.sequence || [];
      const sequenceWithAntardashas = dashas.sequenceWithAntardashas || [];
      const now = new Date();

      const enrichedSequence = sequence.map((dasha, idx) => {
        const isPast = new Date(dasha.endDate) < now;
        const isCurrent = idx === currentIdx;
        const isFuture = !isPast && !isCurrent;

        const antardashaData = sequenceWithAntardashas[idx];
        const allAntardashas = antardashaData?.antardashas || [];

        const currentAntardasha = isCurrent ? allAntardashas.find(a => {
          const startDate = new Date(a.startDate);
          const endDate = new Date(a.endDate);
          return now >= startDate && now < endDate;
        }) : null;

        return {
          ...dasha,
          idx,
          isPast,
          isCurrent,
          isFuture,
          description: getDashaDescription(dasha.lord),
          antardashas: allAntardashas,
          currentAntardasha
        };
      });

      return {
        type: 'parasara',
        sequence: enrichedSequence,
        currentIdx,
        totalCycle: 120
      };
    }

    return null;
  }, [chartData, framework]);

  if (!timelineData) {
    return <div className="text-white/60 text-center py-8 text-sm">Timeline data unavailable</div>;
  }

  const activeIdx = selectedIdx !== null ? selectedIdx : timelineData.currentIdx;
  const activeDasha = timelineData.sequence[activeIdx];

  return (
    <div className="w-full space-y-2">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-base font-bold text-white">Life Timeline</h2>
          <p className="text-white/50 text-[10px]">Click any period • Current: {timelineData.sequence[timelineData.currentIdx]?.lord}</p>
        </div>
        <div className="text-[10px] text-white/50 text-right">
          <p>120-year cycle • {timelineData.currentIdx + 1}/9</p>
        </div>
      </div>

      {/* HORIZONTAL TIMELINE */}
      <div className="relative">
        {/* Scrollable timeline */}
        <div className="overflow-x-auto pb-2 -mx-2 px-2">
          <div className="flex gap-1 min-w-max relative">
            {/* Connecting line */}
            <div className="absolute top-8 left-2 right-2 h-0.5 bg-slate-700 -z-0"></div>

            {timelineData.sequence.map((dasha) => {
              const isActive = dasha.idx === activeIdx;
              const isCurrent = dasha.isCurrent;

              return (
                <button
                  key={dasha.idx}
                  onClick={() => setSelectedIdx(dasha.idx)}
                  className={`relative flex flex-col items-center gap-0.5 px-1.5 py-0.5 rounded transition min-w-[70px] ${
                    isActive ? 'bg-purple-900/30' : 'hover:bg-slate-800/50'
                  }`}
                >
                  <p className="text-white/40 text-[8px] font-mono">
                    {new Date(dasha.startDate).getFullYear()}
                  </p>

                  <div className="relative z-10">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] border-2 ${
                        isCurrent
                          ? 'border-purple-400 ring-2 ring-purple-400/50'
                          : isActive
                            ? 'border-white'
                            : dasha.isPast ? 'border-slate-600 opacity-60' : 'border-slate-700'
                      }`}
                      style={{ backgroundColor: getDashaColor(dasha.lord) }}
                    >
                      <span className="text-white">{getPlanetSymbol(dasha.lord)}</span>
                    </div>
                    {isCurrent && (
                      <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                    )}
                  </div>

                  <p className={`text-[9px] font-semibold ${
                    isActive ? 'text-white' : isCurrent ? 'text-purple-300' : 'text-white/60'
                  }`}>
                    {dasha.lord}
                  </p>
                  <p className="text-white/40 text-[8px]">{dasha.period}y</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* SELECTED DASHA DETAILS */}
      {activeDasha && (
        <div className={`rounded-lg p-3 border-2 ${
          activeDasha.isCurrent
            ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-purple-500/60'
            : 'bg-slate-800/40 border-slate-700'
        }`}>
          {/* Mahadasha Header */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-lg font-bold text-white text-xl flex items-center justify-center shadow-lg"
                style={{ backgroundColor: getDashaColor(activeDasha.lord) }}
              >
                {getPlanetSymbol(activeDasha.lord)}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: activeDasha.isCurrent ? '#c4b5fd' : '#94a3b8' }}>
                  {activeDasha.isCurrent ? '⚡ Current Mahadasha' : activeDasha.isPast ? '✓ Past Mahadasha' : '→ Future Mahadasha'}
                </p>
                <p className="text-white text-base font-bold">{activeDasha.lord} Mahadasha</p>
                <p className="text-white/60 text-xs font-mono">{formatDate(activeDasha.startDate)} → {formatDate(activeDasha.endDate)} ({activeDasha.period} yrs)</p>
              </div>
            </div>
            {activeDasha.isCurrent && (
              <div className="text-right">
                <p className="text-purple-300 text-xs font-semibold">{getDaysRemaining(activeDasha.endDate)} days left</p>
                <div className="h-1.5 w-32 bg-white/10 rounded-full overflow-hidden mt-1">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                    style={{
                      width: `${Math.min(100, ((new Date() - new Date(activeDasha.startDate)) / (new Date(activeDasha.endDate) - new Date(activeDasha.startDate))) * 100)}%`
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Mahadasha Influence */}
          <div className="bg-white/5 rounded p-2 mb-3">
            <p className="text-[10px] text-white/60 uppercase tracking-wider font-bold mb-1">Mahadasha Influence</p>
            <p className="text-white/90 text-xs leading-relaxed">{activeDasha.description}</p>
          </div>

          {/* ALL ANTARDASHAS */}
          {activeDasha.antardashas && activeDasha.antardashas.length > 0 && (
            <div>
              <p className="text-[10px] text-white/60 uppercase tracking-wider font-bold mb-2 flex items-center gap-2">
                <span>Sub-Periods (Antardashas)</span>
                <span className="text-purple-300 text-[9px] normal-case font-normal">— Click "Ask AI-Strologer" for personalized insights</span>
              </p>
              <div className="grid gap-1.5">
                {activeDasha.antardashas.map((ad, idx) => {
                  const now = new Date();
                  const adStart = new Date(ad.startDate);
                  const adEnd = new Date(ad.endDate);
                  const isPast = adEnd < now;
                  const isCurrentAd = now >= adStart && now < adEnd;
                  const isFuture = adStart > now;

                  return (
                    <div
                      key={idx}
                      className={`rounded p-2 border flex items-center justify-between gap-3 transition ${
                        isCurrentAd
                          ? 'bg-purple-500/20 border-purple-400/60 shadow-lg shadow-purple-500/20'
                          : isPast
                            ? 'bg-slate-800/40 border-slate-700/50 opacity-80'
                            : 'bg-slate-800/30 border-slate-700/50'
                      }`}
                    >
                      {/* Left: Status icon + Antardasha info */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="text-base">
                          {isCurrentAd ? '🔵' : isPast ? '✓' : '○'}
                        </div>
                        <div
                          className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: getDashaColor(ad.lord) }}
                        >
                          {getPlanetSymbol(ad.lord)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-xs font-semibold flex items-center gap-2">
                            {activeDasha.lord}/{ad.lord}
                            {isCurrentAd && (
                              <span className="bg-green-500/30 text-green-300 text-[9px] px-1.5 py-0.5 rounded animate-pulse">NOW</span>
                            )}
                            {isPast && (
                              <span className="bg-slate-600/50 text-white/60 text-[9px] px-1.5 py-0.5 rounded">PAST</span>
                            )}
                            {isFuture && (
                              <span className="bg-blue-500/30 text-blue-300 text-[9px] px-1.5 py-0.5 rounded">UPCOMING</span>
                            )}
                          </p>
                          <p className="text-white/50 text-[10px] font-mono">
                            {formatDate(ad.startDate)} → {formatDate(ad.endDate)} ({ad.period?.toFixed(1)}y)
                          </p>
                        </div>
                      </div>

                      {/* Right: Ask AI-Strologer Button */}
                      <button
                        onClick={() => setAistrologerOpen({ mahadasha: activeDasha, antardasha: ad })}
                        className="flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-[10px] font-semibold px-3 py-1.5 rounded transition shadow-md whitespace-nowrap"
                      >
                        <span>🪔</span>
                        <span>Ask AI-Strologer</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI-STROLOGER MODAL */}
      {aistrologerOpen && (
        <AIStrologer
          chartData={chartData}
          mahadasha={aistrologerOpen.mahadasha}
          antardasha={aistrologerOpen.antardasha}
          onClose={() => setAistrologerOpen(null)}
          onRequireSignup={() => {
            setAistrologerOpen(null);
            if (onRequireSignup) onRequireSignup();
          }}
        />
      )}
    </div>
  );
}

// HELPER FUNCTIONS
function getPlanetSymbol(lord) {
  const symbols = {
    'Sun': '☉', 'Moon': '☽', 'Mercury': '☿', 'Venus': '♀', 'Mars': '♂',
    'Jupiter': '♃', 'Saturn': '♄', 'Rahu': '☊', 'Ketu': '☋'
  };
  return symbols[lord] || lord?.substring(0, 1);
}

function getDashaColor(lord) {
  const colors = {
    'Sun': '#FBBF24', 'Moon': '#60A5FA', 'Mars': '#F87171', 'Mercury': '#34D399',
    'Jupiter': '#A78BFA', 'Venus': '#F472B6', 'Saturn': '#94A3B8', 'Rahu': '#818CF8', 'Ketu': '#06B6D4'
  };
  return colors[lord] || '#999';
}

function getDashaDescription(lord) {
  const descriptions = {
    'Sun': 'Leadership, authority, and prominence. Focus on career growth and personal power.',
    'Moon': 'Emotional peace, motherly influence, and family matters. Time for nurturing relationships.',
    'Mars': 'Courage, action, and energy. Favorable for starting new ventures and overcoming obstacles.',
    'Mercury': 'Communication, intellect, and commerce. Good period for learning and business deals.',
    'Jupiter': 'Expansion, wisdom, and blessings. Excellent for spiritual growth and prosperity.',
    'Venus': 'Comfort, relationships, and pleasures. Favorable for marriage and artistic pursuits.',
    'Saturn': 'Discipline, hard work, and lessons. Period of consolidation and building long-term foundations.',
    'Rahu': 'Ambitions, desires, and unconventional paths. Period of significant changes and growth.',
    'Ketu': 'Spirituality, detachment, and introspection. Time for inner transformation and wisdom.'
  };
  return descriptions[lord] || 'A period of unique planetary influence.';
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDaysRemaining(endDateStr) {
  const endDate = new Date(endDateStr);
  const now = new Date();
  const days = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, days);
}

export default LifeTimeline;
